/**
 * Team Service
 * Handles API calls for team management
 */

import { ApiService } from './api';

export interface TeamMember {
  id: number;
  user_id: number;
  team_id: number;
  role: string;
  position: string;
  department: string;
  join_date: string;
  status: 'active' | 'inactive' | 'on_leave';
  performance_score?: number;
  user: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface Team {
  id: number;
  name: string;
  description: string;
  department: string;
  lead_id?: number;
  status: 'active' | 'inactive' | 'archived';
  members_count: number;
  project_count: number;
  budget?: number;
  performance_score?: number;
  lead?: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  members?: TeamMember[];
  created_at: string;
  updated_at: string;
}

export interface TeamStats {
  total_teams: number;
  active_teams: number;
  inactive_teams: number;
  total_members: number;
  departments: string[];
  average_team_size: number;
  largest_team: {
    name: string;
    members_count: number;
  };
  performance_stats: {
    high_performing: number;
    average_performing: number;
    low_performing: number;
  };
}

export interface CreateTeamData {
  name: string;
  description: string;
  department: string;
  team_lead_id?: number;
  budget?: number;
}

export interface UpdateTeamData {
  name?: string;
  description?: string;
  department?: string;
  team_lead_id?: number;
  status?: 'active' | 'inactive' | 'archived';
  budget?: number;
}

export interface AddTeamMemberData {
  user_id: number;
  role: string;
  position: string;
  department: string;
}

export interface UpdateTeamMemberData {
  role?: string;
  position?: string;
  department?: string;
  status?: 'active' | 'inactive' | 'on_leave';
}

class TeamService {
  /**
   * Get all teams with pagination
   */
  static async getTeams(params?: {
    page?: number;
    limit?: number;
    search?: string;
    department?: string;
    status?: 'active' | 'inactive' | 'archived';
  }): Promise<{
    teams: Team[];
    total: number;
    current_page: number;
    last_page: number;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data?: {
          data: Team[];
          total: number;
          current_page: number;
          last_page: number;
        };
        teams?: {
          data: Team[];
          total: number;
          current_page: number;
          last_page: number;
        };
      }>('/admin/teams', { params });

      const paginated = response.teams ?? response.data;

      if (!paginated) {
        throw new Error('Invalid teams response format');
      }

      return {
        teams: paginated.data ?? [],
        total: paginated.total ?? paginated.data?.length ?? 0,
        current_page: paginated.current_page ?? 1,
        last_page: paginated.last_page ?? 1
      };
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw new Error('Failed to load teams');
    }
  }

  /**
   * Get a specific team by ID
   */
  static async getTeam(id: number): Promise<Team> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data?: Team;
        team?: Team;
      }>(`/admin/teams/${id}`);

      const team = response.team ?? response.data;

      if (!team) {
        throw new Error('Invalid team response format');
      }

      return team;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw new Error('Failed to load team');
    }
  }

  /**
   * Create a new team
   */
  static async createTeam(data: CreateTeamData): Promise<Team> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        data?: Team;
        team?: Team;
        message: string;
      }>('/admin/teams', data);

      const team = response.team ?? response.data;

      if (!team) {
        throw new Error('Invalid team response format');
      }

      return team;
    } catch (error) {
      console.error('Error creating team:', error);
      throw new Error('Failed to create team');
    }
  }

  /**
   * Update an existing team
   */
  static async updateTeam(id: number, data: UpdateTeamData): Promise<Team> {
    try {
      const response = await ApiService.put<{
        success: boolean;
        data?: Team;
        team?: Team;
        message: string;
      }>(`/admin/teams/${id}`, data);

      const team = response.team ?? response.data;

      if (!team) {
        throw new Error('Invalid team response format');
      }

      return team;
    } catch (error) {
      console.error('Error updating team:', error);
      throw new Error('Failed to update team');
    }
  }

  /**
   * Delete a team
   */
  static async deleteTeam(id: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/teams/${id}`);
    } catch (error) {
      console.error('Error deleting team:', error);
      throw new Error('Failed to delete team');
    }
  }

  /**
   * Get team members
   */
  static async getTeamMembers(teamId: number, params?: {
    page?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'on_leave';
  }): Promise<{
    members: TeamMember[];
    total: number;
    current_page: number;
    last_page: number;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        members: {
          data: TeamMember[];
          total: number;
          current_page: number;
          last_page: number;
        };
      }>(`/admin/teams/${teamId}/members`, { params });
      
      return {
        members: response.members.data,
        total: response.members.total,
        current_page: response.members.current_page,
        last_page: response.members.last_page
      };
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw new Error('Failed to load team members');
    }
  }

  /**
   * Add member to team
   */
  static async addTeamMember(teamId: number, data: AddTeamMemberData): Promise<TeamMember> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        member: TeamMember;
        message: string;
      }>(`/admin/teams/${teamId}/members`, data);
      
      return response.member;
    } catch (error) {
      console.error('Error adding team member:', error);
      throw new Error('Failed to add team member');
    }
  }

  /**
   * Update team member
   */
  static async updateTeamMember(teamId: number, memberId: number, data: UpdateTeamMemberData): Promise<TeamMember> {
    try {
      const response = await ApiService.put<{
        success: boolean;
        member: TeamMember;
        message: string;
      }>(`/admin/teams/${teamId}/members/${memberId}`, data);
      
      return response.member;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw new Error('Failed to update team member');
    }
  }

  /**
   * Remove member from team
   */
  static async removeTeamMember(teamId: number, memberId: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/teams/${teamId}/members/${memberId}`);
    } catch (error) {
      console.error('Error removing team member:', error);
      throw new Error('Failed to remove team member');
    }
  }

  /**
   * Get team statistics
   */
  static async getTeamStats(): Promise<TeamStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data?: TeamStats;
        stats?: TeamStats;
      }>('/admin/teams/stats');

      const stats = response.stats ?? response.data;

      if (!stats) {
        throw new Error('Invalid team stats response format');
      }

      return stats;
    } catch (error) {
      console.error('Error fetching team stats:', error);
      throw new Error('Failed to load team statistics');
    }
  }

  /**
   * Get all departments
   */
  static async getDepartments(): Promise<string[]> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        departments: string[];
      }>('/admin/teams/departments');
      
      return response.departments;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw new Error('Failed to load departments');
    }
  }

  /**
   * Get all departments with full data (using new departments API)
   */
  static async getDepartmentsWithDetails(): Promise<Array<{id: number; name: string; color: string}>> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        departments: Array<{id: number; name: string; color: string}>;
      }>('/admin/departments/list');
      
      return response.departments;
    } catch (error) {
      console.error('Error fetching department details:', error);
      // Fallback to basic departments list
      const basicDepts = await this.getDepartments();
      return basicDepts.map((name, index) => ({ id: index + 1, name, color: '#007bff' }));
    }
  }

  /**
   * Set team lead
   */
  static async setTeamLead(teamId: number, userId: number): Promise<void> {
    try {
      await ApiService.put<{
        success: boolean;
        message: string;
      }>(`/admin/teams/${teamId}/lead`, { user_id: userId });
    } catch (error) {
      console.error('Error setting team lead:', error);
      throw new Error('Failed to set team lead');
    }
  }

  /**
   * Remove team lead
   */
  static async removeTeamLead(teamId: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/teams/${teamId}/lead`);
    } catch (error) {
      console.error('Error removing team lead:', error);
      throw new Error('Failed to remove team lead');
    }
  }

  /**
   * Get team performance metrics
   */
  static async getTeamPerformance(teamId: number, params?: {
    period?: 'week' | 'month' | 'quarter' | 'year';
  }): Promise<{
    overall_score: number;
    productivity: number;
    collaboration: number;
    quality: number;
    timeline_adherence: number;
    member_satisfaction: number;
    trend: 'up' | 'down' | 'stable';
    previous_score?: number;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        performance: {
          overall_score: number;
          productivity: number;
          collaboration: number;
          quality: number;
          timeline_adherence: number;
          member_satisfaction: number;
          trend: 'up' | 'down' | 'stable';
          previous_score?: number;
        };
      }>(`/admin/teams/${teamId}/performance`, { params });
      
      return response.performance;
    } catch (error) {
      console.error('Error fetching team performance:', error);
      throw new Error('Failed to load team performance');
    }
  }

  /**
   * Archive/Unarchive team
   */
  static async toggleTeamArchive(teamId: number): Promise<Team> {
    try {
      const response = await ApiService.put<{
        success: boolean;
        team: Team;
        message: string;
      }>(`/admin/teams/${teamId}/toggle-archive`);
      
      return response.team;
    } catch (error) {
      console.error('Error toggling team archive:', error);
      throw new Error('Failed to toggle team archive status');
    }
  }
}

export default TeamService;
